import { AgentMarketPlaceItemWithFilter } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

export function filterAgentConfigFields(
  agent: Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string; party_id?: string }>
): Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string; party_id?: string }> {
  // Strict whitelist of allowed fields for config.json
  const allowedFields = [
    'party_id',
    'name',
    'party_type',
    'description',
    'tech_stack',
    'category',
    'role',
    'cloud_provider',
    'owners',
    'last_updated',
    'folder_name',
    'downloadable_only',
    'service_name',
    'port',
    'supports_file_upload',
    'prompt1',
    'prompt2',
    'prompt3',
    'detailed_description',
    'icon',
    'isPlatformAgent',
    'file_format_supported',
    'supports_dataset',
    'datasetId',
    'datasetIds',
    'custom_ui',
    'ui_url',
    'download_url',
    'demo_video_url',
    'agents',
  ];

  const filtered: any = {};
  for (const key of allowedFields) {
    // icon and isPlatformAgent are always set
    if (key === 'icon') {
      filtered.icon = 'Cloud';
      continue;
    }
    if (key === 'isPlatformAgent') {
      filtered.isPlatformAgent = false;
      continue;
    }
    // file_format_supported only if supports_file_upload === true
    if (key === 'file_format_supported') {
      if (agent.supports_file_upload === true) {
        filtered.file_format_supported = agent.file_format_supported;
      }
      continue;
    }
    // datasetIds logic - always use datasetIds regardless of new/existing
    if (key === 'datasetId') {
      // Skip datasetId - we only use datasetIds
      continue;
    }
    if (key === 'datasetIds') {
      if (agent.supports_dataset === 'existing' && agent.datasetIds && typeof agent.datasetIds === 'object') {
        // For existing datasets, use the object structure
        filtered.datasetIds = agent.datasetIds;
      } else if (agent.supports_dataset === 'new' && agent.datasetIds && typeof agent.datasetIds === 'string') {
        // For new datasets, use the string value
        filtered.datasetIds = agent.datasetIds;
      } else {
        // If supports_dataset is null or 'not used', datasetIds should be null
        filtered.datasetIds = null;
      }
      continue;
    }
    // custom_ui only if not 'not used' and supports_dataset is not 'existing'
    if (key === 'custom_ui') {
      if (agent.supports_dataset === 'existing') {
        // If supports_dataset is existing, custom_ui should be null
        filtered.custom_ui = null;
      } else if (agent.custom_ui && agent.custom_ui !== 'not used') {
        filtered.custom_ui = agent.custom_ui;
      } else {
        filtered.custom_ui = null;
      }
      continue;
    }
    // ui_url only if custom_ui is 'external'
    if (key === 'ui_url') {
      if (agent.custom_ui === 'external' && agent.ui_url && agent.ui_url.trim() !== '' && agent.ui_url !== 'null') {
        filtered.ui_url = agent.ui_url;
      } else {
        filtered.ui_url = null;
      }
      continue;
    }
    // download_url only if custom_ui is 'external'
    if (key === 'download_url') {
      if (agent.custom_ui === 'external' && agent.download_url && agent.download_url.trim() !== '' && agent.download_url !== 'null') {
        filtered.download_url = agent.download_url;
      } else {
        filtered.download_url = null;
      }
      continue;
    }
    // demo_video_url always present, null if empty or "null" string
    if (key === 'demo_video_url') {
      filtered.demo_video_url = agent.demo_video_url && agent.demo_video_url.trim() !== '' && agent.demo_video_url !== 'null' ? agent.demo_video_url : null;
      continue;
    }
    // agents only if party_type is 'Team'
    if (key === 'agents') {
      if (agent.party_type === 'Team' && (agent as any).agents) {
        filtered.agents = (agent as any).agents;
      }
      continue;
    }
    // party_id: use _id if present
    if (key === 'party_id') {
      filtered.party_id = agent.party_id || agent._id;
      continue;
    }
    // port: default to 8000 if not provided or empty
    if (key === 'port') {
      filtered.port = agent.port !== undefined && agent.port !== null && agent.port !== 0 ? agent.port : 8000;
      continue;
    }
    // Handle prompt fields - convert empty strings and "null" strings to actual null
    if (key === 'prompt1' || key === 'prompt2' || key === 'prompt3') {
      const promptValue = agent[key as keyof typeof agent] as string;
      filtered[key] = promptValue && promptValue.trim() !== '' && promptValue !== 'null' ? promptValue : null;
      continue;
    }
    // All other fields: copy if present
    if (agent[key as keyof typeof agent] !== undefined) {
      filtered[key] = agent[key as keyof typeof agent];
    }
  }
  // supports_dataset always present, set to null if 'not used' or empty
  filtered.supports_dataset = (agent.supports_dataset === 'not used' || !agent.supports_dataset) ? null : agent.supports_dataset;
  return filtered;
}
